import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/context/AuthContext'
import type { SeasonSplit } from '@/lib/competitions'

export interface ManagerStreak {
  userId: string
  streak: number // positive = wins, negative = losses, 0 = no streak
}

export interface ManagerStreaksData {
  overall: Map<string, ManagerStreak>
  apertura: Map<string, ManagerStreak>
  clausura: Map<string, ManagerStreak>
}

type SelectionRow = { matchday: number; result: 'win' | 'draw' | 'loss' }

function computeStreaks(
  byUser: Map<string, SelectionRow[]>,
  startMatchday?: number,
  endMatchday?: number,
): Map<string, ManagerStreak> {
  const streakMap = new Map<string, ManagerStreak>()

  for (const [userId, allSelections] of byUser) {
    const selections = (startMatchday != null && endMatchday != null)
      ? allSelections.filter(s => s.matchday >= startMatchday && s.matchday <= endMatchday)
      : allSelections

    if (selections.length === 0) continue

    // Most recent result first — count consecutive same results
    const firstResult = selections[0].result
    if (firstResult === 'draw') {
      streakMap.set(userId, { userId, streak: 0 })
      continue
    }

    let count = 0
    for (const s of selections) {
      if (s.result === firstResult) count++
      else break
    }

    const streak = firstResult === 'win' ? count : -count
    streakMap.set(userId, { userId, streak })
  }

  return streakMap
}

export function useManagerStreaks(season: string, seasonSplits: SeasonSplit[]) {
  const { currentMembership } = useAuth()
  const leagueId = currentMembership?.league_id

  return useQuery({
    queryKey: ['manager-streaks', leagueId, season, seasonSplits.map(s => `${s.startMatchday}-${s.endMatchday}`).join(',')],
    enabled: !!leagueId && seasonSplits.length > 0,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<ManagerStreaksData> => {
      const { data, error } = await supabase
        .from('user_selections')
        .select('user_id, matchday, match_result')
        .eq('league_id', leagueId!)
        .eq('season', season)
        .eq('processed', true)
        .not('match_result', 'is', null)
        .order('matchday', { ascending: false })

      if (error) throw error

      const byUser = new Map<string, SelectionRow[]>()
      for (const row of data || []) {
        const list = byUser.get(row.user_id) || []
        list.push({ matchday: row.matchday, result: row.match_result as 'win' | 'draw' | 'loss' })
        byUser.set(row.user_id, list)
      }

      const aperturaSplit = seasonSplits[0]
      const clausuraSplit = seasonSplits[1]

      return {
        overall: computeStreaks(byUser),
        apertura: aperturaSplit
          ? computeStreaks(byUser, aperturaSplit.startMatchday, aperturaSplit.endMatchday)
          : new Map(),
        clausura: clausuraSplit
          ? computeStreaks(byUser, clausuraSplit.startMatchday, clausuraSplit.endMatchday)
          : new Map(),
      }
    },
  })
}
